<template>
  <!-- Guided tour overlay: dimmed backdrop with a spotlight cutout + step card.
  Mounted once in App.vue so tours survive route changes. -->
  <div
    v-if="activeStep"
    class="fixed inset-0 tour-overlay-root"
    :class="{ 'tour-overlay--concealed': !tourRevealed && !showLoadingCard }"
  >
    <!-- Backdrop: a rounded box over the spotlight hole whose spread shadow
    dims the rest of the screen (four rectangles can't cut rounded corners).
    Without a target the hole collapses to a point at the viewport center so
    the dim covers the screen, and it animates open when a target is ready (no
    snapping between a full shade and a cutout). -->
    <div
      class="tour-dim"
      :style="dimStyle"
    />
    <!-- Transparent shades around the hole only handle clicks: they block them
    unless the step sets `passthrough` (for controls whose dropdowns spill
    outside the hole); the hole stays interactive on click/hover/event/
    interactive steps. -->
    <div
      v-for="side in ['top', 'left', 'right', 'bottom']"
      :key="`shade-${side}`"
      class="tour-shade"
      :class="{ 'tour-shade--passthrough': targetRect && activeStep.passthrough }"
      :style="shadeStyle(side)"
      @click="dismissEndCard"
    />
    <template v-if="targetRect">
      <div
        v-if="holeBlocked"
        class="absolute"
        :style="holeBlockerStyle"
      />
      <div
        v-if="stepRevealed"
        class="tour-ring"
        :style="ringStyle"
      />
    </template>

    <!-- Outro card: a distinct, centered "chapter break" hinting at what's next -->
    <div
      v-if="isOutro"
      ref="card"
      class="tour-card tour-card--outro absolute rounded-xl bg-white px-6 pt-6 pb-5 text-center"
      :class="cardStateClass"
      :style="cardStyle"
    >
      <!-- End cards can be dismissed without leaving the page -->
      <button
        type="button"
        aria-label="Close"
        class="tour-close absolute top-2 right-2 w-7 h-7 rounded-md flex items-center justify-center text-icon-normal hover:bg-neutral-50 hover:text-icon-muted focus:outline-none"
        @click="dismissEndCard"
      >
        <TagRemoveIcon />
      </button>
      <div class="tour-outro-badge mx-auto">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="18"
          height="18"
          viewBox="0 0 20 20"
          fill="none"
        >
          <path
            d="M4.5 10.5L8 14L15.5 6.5"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </div>
      <BaseText
        v-if="activeStep.kicker"
        type="heading"
        size="overline"
        class="uppercase text-text-subdued mt-4"
      >
        {{ interpolate(activeStep.kicker) }}
      </BaseText>
      <BaseText
        type="heading"
        size="md"
        class="text-text-loud"
        :class="activeStep.kicker ? 'mt-1.5' : 'mt-4'"
      >
        {{ interpolate(activeStep.title) }}
      </BaseText>
      <BaseText
        v-for="(paragraph, index) in bodyParagraphs"
        :key="`outro-p-${index}`"
        type="body"
        size="sm"
        class="text-text-muted"
        :class="index === 0 ? 'mt-2' : 'mt-1.5'"
      >
        {{ interpolate(paragraph) }}
      </BaseText>
      <div class="flex justify-center mt-5">
        <BaseButton
          primary
          class="w-40"
          :forced-theme="tourAccent"
          @click="next"
        >
          {{ activeStep.ctaLabel || 'Continue' }}
        </BaseButton>
      </div>
    </div>

    <!-- Completion: end-of-tour card with a scene of the result in the wild -->
    <div
      v-else-if="isCompletion"
      ref="card"
      class="tour-card tour-card--outro absolute rounded-xl bg-white overflow-hidden"
      :class="cardStateClass"
      :style="cardStyle"
    >
      <!-- End cards can be dismissed without leaving the page -->
      <button
        type="button"
        aria-label="Close"
        class="tour-close absolute top-2 right-2 w-7 h-7 rounded-md flex items-center justify-center text-icon-normal hover:bg-neutral-50 hover:text-icon-muted focus:outline-none"
        @click="dismissEndCard"
      >
        <TagRemoveIcon />
      </button>
      <TourCompletionCard
        :title="interpolate(activeStep.title)"
        :paragraphs="bodyParagraphs.map(interpolate)"
        :kicker="interpolate(activeStep.kicker)"
        :cta-label="activeStep.ctaLabel || 'Continue'"
        :scene="activeStep.scene"
        :pill="interpolate(activeStep.pill)"
        :accent="activeStep.accent || 'blue'"
        :context="getTourContext"
        @continue="next"
      />
    </div>

    <!-- Standard step card -->
    <div
      v-else
      ref="card"
      class="tour-card absolute rounded-xl bg-white p-4"
      :class="cardStateClass"
      :style="cardStyle"
    >
      <BaseText
        type="label"
        size="sm"
        class="text-text-loud"
      >
        {{ interpolate(activeStep.title) }}
      </BaseText>
      <!-- Optional one-click suggestions sit directly under the title -->
      <div
        v-if="activeStep.suggestions && activeStep.suggestions.length"
        class="mt-3"
      >
        <div class="flex flex-wrap gap-1.5">
          <button
            v-for="suggestion in activeStep.suggestions"
            :key="suggestion.value"
            type="button"
            class="tour-suggestion px-2.5 py-1 rounded-full border transition-colors duration-100"
            :class="pickedSuggestion === suggestion.value
              ? 'tour-suggestion--picked'
              : 'border-border-normal bg-white hover:bg-neutral-10'"
            :disabled="!!pickedSuggestion"
            @click="pickSuggestion(suggestion)"
          >
            <BaseText
              type="label"
              size="xs"
              :class="pickedSuggestion === suggestion.value ? 'text-white' : 'text-text-muted'"
            >
              {{ suggestion.label }}
            </BaseText>
          </button>
        </div>
        <BaseText
          type="body"
          size="sm"
          class="text-text-subdued mt-2"
        >
          Or type your own and press Enter.
        </BaseText>
      </div>
      <BaseText
        v-for="(paragraph, index) in bodyParagraphs"
        :key="`step-p-${index}`"
        type="body"
        size="sm"
        class="text-text-muted"
        :class="index === 0 ? 'mt-1.5' : 'mt-2'"
      >
        {{ interpolate(paragraph) }}
      </BaseText>
      <!-- Optional feature list: label + one-line value statement each -->
      <ul
        v-if="activeStep.items && activeStep.items.length"
        class="tour-items mt-3 flex flex-col gap-2.5"
      >
        <li
          v-for="item in activeStep.items"
          :key="item.label"
          class="flex items-start gap-2.5 ml-0"
        >
          <span class="tour-item-dot" />
          <div class="min-w-0">
            <BaseText
              type="label"
              size="sm"
              class="text-text-loud"
            >
              {{ item.label }}
            </BaseText>
            <BaseText
              type="body"
              size="sm"
              class="text-text-muted"
            >
              {{ interpolate(item.text) }}
            </BaseText>
          </div>
        </li>
      </ul>
      <div class="flex items-center justify-between gap-3 mt-3.5">
        <div class="flex items-center gap-2 flex-shrink-0 whitespace-nowrap">
          <button
            class="text-text-subdued underline transition-colors duration-100 hover:text-text-muted"
            @click="skip()"
          >
            <BaseText
              type="body"
              size="xs"
            >
              Skip tour
            </BaseText>
          </button>
          <BaseText
            v-if="tour.steps.length > 1"
            type="body"
            size="xs"
            class="text-text-subdued"
          >
            {{ stepIndex + 1 }} of {{ tour.steps.length }}
          </BaseText>
        </div>
        <!-- Target + ready waits share one loading footer; Next stays hidden -->
        <div
          v-if="isWaiting"
          class="flex items-center gap-2"
        >
          <BaseLoadingSpinnerCircle
            :width="16"
            :height="16"
            class="flex-shrink-0 text-icon-normal"
          />
          <BaseText
            type="body"
            size="sm"
            class="text-text-subdued"
          >
            {{ interpolate(activeStep.readyLabel || 'Loading…') }}
          </BaseText>
        </div>
        <BaseButton
          v-else-if="showNextButton"
          primary
          :forced-theme="tourAccent"
          @click="next"
        >
          {{ activeStep.ctaLabel || 'Next' }}
        </BaseButton>
        <!-- Interaction steps: a quiet hint about what unlocks the next step -->
        <BaseText
          v-else-if="interactionHint"
          type="body"
          size="xs"
          class="text-text-subdued text-right"
        >
          {{ interpolate(interactionHint) }}
        </BaseText>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import posthog from 'posthog-js'
import EventBus from '@/plugins/BaseNotifications/EventBus'
import TourCompletionCard from './TourCompletionCard.vue'
import TagRemoveIcon from '../globals/Icons/TagRemoveIcon.vue'
import {
  findVisibleTarget,
  findAllVisibleTargets,
  isAbortError,
  waitForCondition,
  waitForAnimations,
  waitForStableLayout
} from '@/utils/setup/tourReadiness'

const SPOTLIGHT_PADDING = 8
const CARD_GAP = 14
const CARD_WIDTH = 320
const CARD_WIDTH_WITH_ITEMS = 360
const CARD_WIDTH_OUTRO = 400
const CARD_WIDTH_COMPLETION = 460
const VIEWPORT_MARGIN = 16
// Fallbacks for `{placeholder}` copy when the tour context lacks a value
const CONTEXT_DEFAULTS = {
  brandName: 'the brand',
  boardName: 'your board'
}
// Steps the tour stamps on completion (POST /setup/stamp). nginx caches
// identical Discovery searches (the tour's suggestion chips), so the server's
// own search stamp can miss. The demo Lens and Ad Rank steps are stamped when
// their tour starts (SetupView). Must be in STAMPABLE_STEPS in ForeplayServer
// services/setup/config.js.
const STAMP_ON_FINISH_STEPS = ['discovery_search']
const TARGET_TIMEOUT_MS = 12000
// Upper bound on waiting for a found target's animations / layout to settle
const SETTLE_TIMEOUT_MS = 2000
// Steps that gate on page data (e.g. analytics) get longer before we give up
// and show the step anyway
const READY_TIMEOUT_MS = 45000
// Only show the loading card when a step is slow to become ready, so fast
// steps appear in one go instead of flashing a spinner
const LOADING_CARD_DELAY_MS = 400

export default {
  name: 'TourOverlay',
  components: {
    TourCompletionCard,
    TagRemoveIcon
  },
  data () {
    return {
      targetEls: [],
      targetRect: null,
      cardHeight: 160,
      // True while a step's route is still being pushed — the page (and its
      // theme) hasn't switched yet, so the CTA stays hidden
      preparingStep: false,
      waitingForTarget: false,
      waitingForReady: false,
      // 'hover-target' steps unlock their Next button once the user has
      // hovered the spotlit element
      targetHovered: false,
      // Set when a step's target never showed up: the card centers and gets
      // a Next button so the tour can't dead-end on an interaction step
      targetMissing: false,
      // Suggestion chip the user clicked on this step (highlights it while
      // the page reacts)
      pickedSuggestion: null,
      // Last tour:step-enter payload — leave must use this, not activeStep
      // (the watcher tears down after Vuex already advanced)
      enteredStepMeta: null,
      // The current step's card is positioned and visible
      stepRevealed: false,
      // The tour has shown at least once; the shade then stays up between steps
      tourRevealed: false,
      // A step is taking a while: show the shade + loading card meanwhile
      showLoadingCard: false,
      // The card has been placed for this step, so later moves may animate
      cardPlaced: false,
      viewport: { width: window.innerWidth, height: window.innerHeight }
    }
  },
  computed: {
    ...mapGetters('TourModule', ['getActiveTour', 'getActiveTourStep', 'getTourStepIndex', 'getTourContext']),
    tour () {
      return this.getActiveTour
    },
    // Product accent for the tour's buttons, so they never pick up the
    // theme of the page the tour started on
    tourAccent () {
      if (!this.tour) return ''
      return this.activeStep?.accent ||
        this.tour.steps.find((step) => step.accent)?.accent ||
        ''
    },
    activeStep () {
      return this.getActiveTourStep
    },
    stepIndex () {
      return this.getTourStepIndex
    },
    isOutro () {
      return this.activeStep?.variant === 'outro'
    },
    isCompletion () {
      return this.activeStep?.variant === 'completion'
    },
    isEndCard () {
      return this.isOutro || this.isCompletion
    },
    // `body` may be one string or a list of short paragraphs
    bodyParagraphs () {
      const body = this.activeStep?.body
      if (!body) return []
      return Array.isArray(body) ? body.filter(Boolean) : [body]
    },
    // Every [data-tour] name this step spotlights (union of `target`/`targets`)
    stepTargets () {
      const step = this.activeStep
      if (!step) return []
      if (Array.isArray(step.targets)) return step.targets
      return step.target ? [step.target] : []
    },
    // Extra [data-tour] names folded into the spotlight whenever they exist
    // (e.g. a tooltip that appears on hover) — never waited on
    optionalTargets () {
      const extra = this.activeStep?.optionalTargets
      if (!extra) return []
      return Array.isArray(extra) ? extra : [extra]
    },
    // Whether the page under the spotlight can be interacted with
    holeBlocked () {
      const step = this.activeStep
      if (!step) return true
      if (step.interactive) return false
      return !['click-target', 'hover-target', 'event'].includes(step.advance)
    },
    // Hidden until the step is ready (or slow enough to show the loading card);
    // position changes only animate once the card has been placed
    cardStateClass () {
      return {
        'tour-card--hidden': !this.stepRevealed && !this.showLoadingCard,
        'tour-card--placed': this.cardPlaced
      }
    },
    isWaiting () {
      return this.preparingStep || this.waitingForTarget || this.waitingForReady
    },
    showNextButton () {
      const step = this.activeStep
      if (!step || this.isWaiting) return false
      if (step.advance === 'next') return true
      if (this.targetMissing && step.advance !== 'event') return true
      return step.advance === 'hover-target' && this.targetHovered
    },
    interactionHint () {
      const step = this.activeStep
      if (!step || this.targetMissing || this.isWaiting) return null
      if (step.hint) return step.hint
      if (step.advance === 'hover-target') return 'Hover the highlight to continue'
      if (step.advance === 'click-target') return 'Click the highlight to continue'
      if (step.advance === 'event') return 'Complete the step to continue'
      return null
    },
    cardWidth () {
      if (this.isCompletion) return CARD_WIDTH_COMPLETION
      if (this.isOutro) return CARD_WIDTH_OUTRO
      if (this.activeStep?.items?.length) return CARD_WIDTH_WITH_ITEMS
      return CARD_WIDTH
    },
    // Spotlight rect with padding, clamped to the viewport
    holeRect () {
      if (!this.targetRect) return null
      const r = this.targetRect
      return {
        top: Math.max(0, r.top - SPOTLIGHT_PADDING),
        left: Math.max(0, r.left - SPOTLIGHT_PADDING),
        right: Math.min(this.viewport.width, r.right + SPOTLIGHT_PADDING),
        bottom: Math.min(this.viewport.height, r.bottom + SPOTLIGHT_PADDING)
      }
    },
    // The shades' hole: the spotlight, or a zero-size point at the viewport
    // center (full coverage) that animates open once a target is ready
    shadeHole () {
      if (this.holeRect) return this.holeRect
      const centerX = this.viewport.width / 2
      const centerY = this.viewport.height / 2
      return { top: centerY, left: centerX, right: centerX, bottom: centerY }
    },
    holeBlockerStyle () {
      const h = this.holeRect
      if (!h) return {}
      return {
        top: `${h.top}px`,
        left: `${h.left}px`,
        width: `${h.right - h.left}px`,
        height: `${h.bottom - h.top}px`,
        borderRadius: '12px'
      }
    },
    ringStyle () {
      return this.holeBlockerStyle
    },
    dimStyle () {
      const h = this.shadeHole
      return {
        top: `${h.top}px`,
        left: `${h.left}px`,
        width: `${h.right - h.left}px`,
        height: `${h.bottom - h.top}px`
      }
    },
    cardStyle () {
      const width = `${this.cardWidth}px`
      const h = this.holeRect
      const placement = this.activeStep?.cardPlacement || 'auto'
      // Centered card for target-less/outro steps, while a target is loading,
      // or when the step asks for it (e.g. a spotlight that fills the page)
      if (!h || placement === 'center') {
        return {
          width,
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)'
        }
      }

      const vw = this.viewport.width
      const vh = this.viewport.height
      const cardH = this.cardHeight
      const cardW = this.cardWidth
      const clampTop = (top) => Math.min(Math.max(VIEWPORT_MARGIN, top), vh - cardH - VIEWPORT_MARGIN)
      const clampLeft = (left) => Math.min(Math.max(VIEWPORT_MARGIN, left), vw - cardW - VIEWPORT_MARGIN)

      const fitsBelow = h.bottom + CARD_GAP + cardH < vh - VIEWPORT_MARGIN
      const fitsAbove = h.top - CARD_GAP - cardH > VIEWPORT_MARGIN
      const fitsRight = h.right + CARD_GAP + cardW < vw - VIEWPORT_MARGIN
      const fitsLeft = h.left - CARD_GAP - cardW > VIEWPORT_MARGIN

      let side = placement
      if (side === 'auto') {
        if (fitsBelow) side = 'below'
        else if (fitsAbove) side = 'above'
        else if (fitsRight) side = 'right'
        else if (fitsLeft) side = 'left'
        else side = 'below'
      }

      switch (side) {
        case 'right':
          return {
            width,
            top: `${clampTop(h.top + (h.bottom - h.top - cardH) / 2)}px`,
            left: `${clampLeft(h.right + CARD_GAP)}px`
          }
        case 'left':
          return {
            width,
            top: `${clampTop(h.top + (h.bottom - h.top - cardH) / 2)}px`,
            left: `${clampLeft(h.left - CARD_GAP - cardW)}px`
          }
        case 'above':
          return {
            width,
            top: `${clampTop(h.top - CARD_GAP - cardH)}px`,
            left: `${clampLeft(h.left)}px`
          }
        case 'below':
        default:
          return {
            width,
            top: `${clampTop(h.bottom + CARD_GAP)}px`,
            left: `${clampLeft(h.left)}px`
          }
      }
    }
  },
  watch: {
    tour: {
      handler (tour) {
        this.bindTourEvents(tour)
        this.bindHistoryNavigation(tour)
        if (!tour) this.tourRevealed = false
      },
      immediate: true
    },
    activeStep: {
      handler (step) {
        this.teardownStep()
        if (step) this.prepareStep(step)
      },
      immediate: true
    }
  },
  mounted () {
    window.addEventListener('resize', this.updateViewport)
  },
  beforeDestroy () {
    window.removeEventListener('resize', this.updateViewport)
    this.teardownStep()
    this.unbindTourEvents()
    this.bindHistoryNavigation(null)
  },
  methods: {
    ...mapActions('TourModule', ['advanceTour', 'goToTourStep', 'endTour', 'updateTourContext']),
    ...mapActions('SetupModule', ['fetchSetupProgress', 'markStepCompleted', 'stampStep']),
    updateViewport () {
      this.viewport = { width: window.innerWidth, height: window.innerHeight }
    },
    interpolate (text) {
      if (!text) return ''
      const context = this.getTourContext || {}
      return text.replace(/\{(\w+)\}/g, (match, key) => {
        const value = context[key]
        if (value !== undefined && value !== null && value !== '') return value
        return CONTEXT_DEFAULTS[key] !== undefined ? CONTEXT_DEFAULTS[key] : match
      })
    },
    resolveRoute (route) {
      const params = {}
      Object.keys(route.params || {}).forEach((key) => {
        const value = route.params[key]
        params[key] = value === '{brandId}' ? this.getTourContext.brandId : value
      })
      return { name: route.name, params }
    },
    // Runs a step through readiness before revealing it: route settled ->
    // targets in the DOM and visible -> animations finished -> layout steady.
    // Each wait is cancelled (AbortError) when the step changes.
    async prepareStep (step) {
      posthog.capture('Setup Tour Step Viewed', {
        tour_id: this.tour.id,
        step_id: step.id,
        step_index: this.stepIndex
      })

      this.stepAbort = new AbortController()
      const { signal } = this.stepAbort
      this.loadingCardTimer = setTimeout(() => {
        if (this.stepRevealed) return
        // The previous step's hole may point at a page that's gone
        this.targetRect = null
        this.showLoadingCard = true
        // The shade is up now; keep it up (no fade out/in) through the reveal
        this.tourRevealed = true
      }, LOADING_CARD_DELAY_MS)

      try {
        if (step.route) {
          const target = this.resolveRoute(step.route)
          const sameParams = Object.keys(target.params).every((key) => this.$route.params[key] === target.params[key])
          if (this.$route.name !== target.name || !sameParams) {
            this.preparingStep = true
            try {
              await this.$router.push(target)
            } catch (error) {
              // Redirected or duplicate navigation — the step still shows
            }
            if (signal.aborted) return
            this.preparingStep = false
          }
        }

        // Let the new route's components render before looking for targets
        await this.$nextTick()
        if (signal.aborted) return

        // Host surfaces (FilterTopBar, etc.) open their UI for the spotlight
        this.emitStepEnter(step)

        if (!this.stepTargets.length) {
          this.targetRect = null
          this.revealStep(signal)
          this.waitForReady(step, signal)
          return
        }

        await this.waitForTargets(step, signal)
      } catch (error) {
        if (!isAbortError(error)) throw error
      }
    },
    // Target elements for the step, or null until every one is visible
    findStepTargets (step) {
      if (step.matchAll) {
        const els = findAllVisibleTargets(this.stepTargets[0])
        return els.length ? els : null
      }
      const els = this.stepTargets.map(findVisibleTarget)
      return els.every(Boolean) ? els : null
    },
    // Optional targets present right now (e.g. a dropdown the step opened)
    findOptionalTargets () {
      return this.optionalTargets.map(findVisibleTarget).filter(Boolean)
    },
    async waitForTargets (step, signal) {
      this.waitingForTarget = true
      let els = await waitForCondition(() => this.findStepTargets(step), { signal, timeoutMs: TARGET_TIMEOUT_MS })
      // Timed out: spotlight whichever targets did show up
      if (!els) els = this.stepTargets.map(findVisibleTarget).filter(Boolean)

      if (!els.length) {
        this.waitingForTarget = false
        // Nothing appeared — fall back to a centered card so the tour stays functional
        this.targetMissing = true
        this.targetRect = null
        this.revealStep(signal)
        return
      }

      // Found: the loading card (if it showed) fades out while the target
      // settles, then the step fades in already in place
      clearTimeout(this.loadingCardTimer)
      this.showLoadingCard = false

      this.targetEls = els
      this.scrollTargetIntoView(els[0], step)

      // Wait out entrance animations (page fade, dropdown opening), then for
      // the scroll and any late layout shifts to come to rest
      const settleEls = () => [...this.targetEls, ...this.findOptionalTargets()]
      await waitForAnimations(settleEls(), { signal, timeoutMs: SETTLE_TIMEOUT_MS })
      await waitForStableLayout(
        () => settleEls().map((el) => el.getBoundingClientRect()),
        { signal, timeoutMs: SETTLE_TIMEOUT_MS }
      )

      this.waitingForTarget = false
      this.startTracking()
      this.revealStep(signal)
      this.waitForReady(step, signal)
    },
    // Measure the card at its final size, then show it in place (no slide
    // from the center); moves after this point animate
    // (the signal guards against a step change landing before the next tick)
    revealStep (signal) {
      this.$nextTick(() => {
        if (signal.aborted) return
        if (this.$refs.card) this.cardHeight = this.$refs.card.offsetHeight
        clearTimeout(this.loadingCardTimer)
        this.showLoadingCard = false
        this.stepRevealed = true
        this.tourRevealed = true
        requestAnimationFrame(() => {
          if (!signal.aborted) this.cardPlaced = true
        })
      })
    },
    emitStepEnter (step) {
      if (!step || !this.tour) return
      this.enteredStepMeta = {
        tourId: this.tour.id,
        stepId: step.id,
        openFilterDropdown: !!step.openFilterDropdown,
        openGroupBy: !!step.openGroupBy,
        openSortDropdown: !!step.openSortDropdown,
        openSidebarCreate: !!step.openSidebarCreate
      }
      EventBus.$emit('tour:step-enter', this.enteredStepMeta)
    },
    emitStepLeave () {
      if (!this.enteredStepMeta) return
      EventBus.$emit('tour:step-leave', this.enteredStepMeta)
      this.enteredStepMeta = null
    },
    // Names of targets that must carry [data-tour-ready] before the step's
    // CTA appears. `waitForReady: true` means all of them; a string or array
    // names specific ones.
    readyTargets (step) {
      const wait = step.waitForReady
      if (!wait) return []
      if (wait === true) return this.stepTargets
      return Array.isArray(wait) ? wait : [wait]
    },
    // Hold the CTA until the named targets report they've finished loading
    async waitForReady (step, signal) {
      const names = this.readyTargets(step)
      if (!names.length) return
      this.waitingForReady = true
      const allReady = () => names.every((name) => document.querySelector(`[data-tour="${name}"][data-tour-ready]`))
      try {
        await waitForCondition(allReady, { signal, timeoutMs: READY_TIMEOUT_MS })
      } catch (error) {
        if (!isAbortError(error)) throw error
        return
      }
      this.waitingForReady = false
      this.measureCardSoon()
    },
    scrollTargetIntoView (el, step) {
      // Tall targets (a whole page section) should reveal from their top;
      // small ones look best centered
      const tall = el.getBoundingClientRect().height > window.innerHeight * 0.6
      el.scrollIntoView({ behavior: 'smooth', block: step.scrollBlock || (tall ? 'start' : 'center') })
    },
    // Track the union of the targets' rects every frame: follows scrolling,
    // resizes and layout shifts without listener bookkeeping
    startTracking () {
      const track = () => {
        if (!this.targetEls.length || !this.activeStep) return
        // Optional targets come and go (hover tooltips) — re-query each frame
        const optionalEls = this.findOptionalTargets()
        const rects = [...this.targetEls, ...optionalEls].map((el) => el.getBoundingClientRect())
        const rect = {
          top: Math.min(...rects.map((r) => r.top)),
          left: Math.min(...rects.map((r) => r.left)),
          right: Math.max(...rects.map((r) => r.right)),
          bottom: Math.max(...rects.map((r) => r.bottom))
        }
        rect.width = rect.right - rect.left
        rect.height = rect.bottom - rect.top
        const prev = this.targetRect
        if (!prev || rect.top !== prev.top || rect.left !== prev.left ||
            rect.width !== prev.width || rect.height !== prev.height) {
          this.targetRect = rect
        }
        this.trackingFrame = requestAnimationFrame(track)
      }
      track()

      if (this.activeStep.advance === 'click-target') {
        this.clickHandler = (event) => {
          const hit = this.targetEls.some((el) => el.contains(event.target))
          if (!hit) return
          // A disabled control (e.g. a submit button waiting on input) is
          // not a real advance
          const control = event.target.closest('button, [role="button"], input, a')
          if (control && control.disabled) return
          // Let the app's own handler run first
          setTimeout(() => this.next(), this.activeStep.clickDelay || 50)
        }
        document.addEventListener('click', this.clickHandler, true)
      }

      if (this.activeStep.advance === 'hover-target') {
        this.hoverHandler = () => {
          if (this.targetHovered) return
          this.targetHovered = true
          this.measureCardSoon()
        }
        this.hoverEls = [...this.targetEls]
        this.hoverEls.forEach((el) => el.addEventListener('mouseenter', this.hoverHandler))
      }
    },
    teardownStep () {
      this.emitStepLeave()
      if (this.stepAbort) this.stepAbort.abort()
      clearTimeout(this.loadingCardTimer)
      this.stepRevealed = false
      this.showLoadingCard = false
      this.cardPlaced = false
      this.preparingStep = false
      this.waitingForTarget = false
      this.waitingForReady = false
      this.targetHovered = false
      this.targetMissing = false
      this.pickedSuggestion = null
      if (this.trackingFrame) cancelAnimationFrame(this.trackingFrame)
      if (this.clickHandler) {
        document.removeEventListener('click', this.clickHandler, true)
        this.clickHandler = null
      }
      if (this.hoverHandler) {
        (this.hoverEls || []).forEach((el) => el.removeEventListener('mouseenter', this.hoverHandler))
        this.hoverHandler = null
        this.hoverEls = []
      }
      this.targetEls = []
      // targetRect is kept so the shade glides from the previous hole to the
      // next step's target instead of flashing closed in between
    },
    // Event-driven steps advance when the app reports the action happened (a
    // search was submitted, a board was created…). Listeners live for the
    // whole tour: if the user gets ahead of the walkthrough (hits Enter on
    // the board name before we asked them to create it), the tour catches up
    // instead of stalling on a step whose moment has passed.
    bindTourEvents (tour) {
      this.unbindTourEvents()
      if (!tour) return
      this.tourEventBindings = tour.steps
        .map((step, index) => ({ step, index }))
        .filter(({ step }) => step.advance === 'event' && step.event)
        .map(({ step, index }) => {
          const handler = (payload) => {
            if (this.tour !== tour || this.stepIndex > index) return
            if (payload && typeof payload === 'object') this.updateTourContext(payload)
            this.goTo(index + 1)
          }
          EventBus.$on(step.event, handler)
          return { event: step.event, handler }
        })
    },
    unbindTourEvents () {
      (this.tourEventBindings || []).forEach(({ event, handler }) => EventBus.$off(event, handler))
      this.tourEventBindings = []
    },
    measureCardSoon () {
      this.$nextTick(() => {
        if (this.$refs.card) this.cardHeight = this.$refs.card.offsetHeight
      })
    },
    shadeStyle (side) {
      const h = this.shadeHole
      switch (side) {
        case 'top':
          return { top: 0, left: 0, right: 0, height: `${h.top}px` }
        case 'bottom':
          return { top: `${h.bottom}px`, left: 0, right: 0, bottom: 0 }
        case 'left':
          return { top: `${h.top}px`, left: 0, width: `${h.left}px`, height: `${h.bottom - h.top}px` }
        case 'right':
          return { top: `${h.top}px`, left: `${h.right}px`, right: 0, height: `${h.bottom - h.top}px` }
        default:
          return {}
      }
    },
    async next () {
      if (this.activeStep?.exitToSetup) {
        this.finish(false, { returnToSetup: true })
        return
      }
      const finished = await this.advanceTour()
      if (finished) this.finish(true)
    },
    // One-click chip under the body: emit the step's suggestionEvent so the
    // host page (e.g. Discovery) can run the action and advance an event step
    pickSuggestion (suggestion) {
      if (!suggestion || this.pickedSuggestion) return
      const event = this.activeStep?.suggestionEvent
      if (!event) return
      this.pickedSuggestion = suggestion.value
      EventBus.$emit(event, { keyword: suggestion.value, value: suggestion.value, label: suggestion.label })
      posthog.capture('Setup Tour Suggestion Clicked', {
        tour_id: this.tour?.id,
        step_id: this.activeStep?.id,
        suggestion: suggestion.value
      })
    },
    async goTo (index) {
      const finished = await this.goToTourStep(index)
      if (finished) this.finish(true)
    },
    // Browser back / forward (popstate) means the user left the walkthrough.
    // The tour's own navigation uses router.push and in-page URL syncs use
    // history.replaceState; neither fires popstate, so they never end a tour.
    bindHistoryNavigation (tour) {
      window.removeEventListener('popstate', this.handleHistoryNavigation)
      if (tour) window.addEventListener('popstate', this.handleHistoryNavigation)
    },
    handleHistoryNavigation () {
      if (this.tour) this.skip('history_navigation')
    },
    // `reason` tells analytics how the tour was abandoned
    skip (reason = 'skip_button') {
      posthog.capture('Setup Tour Skipped', {
        tour_id: this.tour.id,
        step_id: this.activeStep?.id,
        step_index: this.stepIndex,
        reason
      })
      this.finish(false)
    },
    // End card closed with the X or a click on the backdrop: the walkthrough
    // counts as done, but the user stays on the current page
    dismissEndCard () {
      if (!this.isEndCard || !this.stepRevealed) return
      posthog.capture('Setup Tour End Card Dismissed', { tour_id: this.tour.id, step_id: this.activeStep.id })
      this.finish(true, { stayOnPage: true })
    },
    finish (completed, { returnToSetup = false, stayOnPage = false } = {}) {
      const tour = this.tour
      if (completed) {
        posthog.capture('Setup Tour Completed', { tour_id: tour.id })
        // Flip the checklist item immediately so the Launchpad greets the
        // user with it already done; the refetch below confirms it
        if (STAMP_ON_FINISH_STEPS.includes(tour.completesStep)) this.stampStep(tour.completesStep)
        else if (tour.completesStep) this.markStepCompleted(tour.completesStep)
      }
      this.endTour()
      // Progress may have changed during the tour (track, save, share…)
      this.fetchSetupProgress({ force: true })
      const goToSetup = (completed && tour.returnToSetup) || returnToSetup
      if (goToSetup && !stayOnPage && this.$route.name !== 'SetupView') {
        this.$router.push({ name: 'SetupView' }).catch(() => {})
      }
    }
  }
}
</script>

<style scoped>
.tour-overlay-root {
  z-index: 500010;
  pointer-events: none;
}
.tour-dim {
  position: absolute;
  pointer-events: none;
  /* Same radius as .tour-ring so the cutout and the ring line up */
  border-radius: 10px;
  box-shadow: 0 0 0 9999px rgba(6, 7, 16, 0.56);
  transition: all 0.25s ease;
}
.tour-shade {
  position: absolute;
  pointer-events: auto;
  transition: all 0.25s ease;
}
.tour-overlay-root .tour-shade--passthrough {
  pointer-events: none;
}
.tour-overlay-root .absolute {
  pointer-events: auto;
}
.tour-ring {
  position: absolute;
  pointer-events: none;
  border-radius: 10px;
  box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.9);
  transition: all 0.25s ease;
}
.tour-card {
  pointer-events: auto;
  box-shadow: 0 12px 32px rgba(6, 7, 16, 0.24);
  transition: opacity 0.16s ease;
}
.tour-card--placed {
  transition: opacity 0.16s ease, top 0.25s ease, left 0.25s ease, width 0.25s ease;
}
.tour-overlay-root .tour-card--hidden {
  opacity: 0;
  pointer-events: none;
}
.tour-overlay--concealed .tour-dim {
  opacity: 0;
}
.tour-card--outro {
  animation: tour-outro-in 320ms cubic-bezier(0.2, 0.8, 0.2, 1) both;
}
.tour-outro-badge {
  width: 40px;
  height: 40px;
  border-radius: 9999px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: theme('colors.white');
  background-color: theme('colors.primary.green-100');
  box-shadow: 0 0 0 6px rgba(0, 168, 120, 0.12);
}
.tour-items li {
  list-style: none;
}
.tour-item-dot {
  flex-shrink: 0;
  width: 6px;
  height: 6px;
  margin-top: 6px;
  border-radius: 9999px;
  background-color: theme('colors.primary.red-100');
}
.tour-suggestion--picked {
  border-color: theme('colors.primary.purple-100');
  background-color: theme('colors.primary.purple-100');
}
.tour-close {
  z-index: 1;
  transition: background-color 150ms ease, color 150ms ease;
}
@keyframes tour-outro-in {
  from {
    opacity: 0;
    transform: translate(-50%, -50%) scale(0.96);
  }
  to {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1);
  }
}
</style>
